import Vue from 'vue'

import SettingsApi from '@/api/settingsApi'
const settingsApi = new SettingsApi()

class IndexedDBService {
  constructor() {
    this.indexedDB = null
    this.dbVersion = 1
    this.dbName = null
    this.storeNames = {
      itemProps: 'itemProps',
      dopProps: 'dopProps',
      changedKeys: 'changedKeys'
    }

    // Таймер и флаг для changedKeys
    this.changedKeysTimer = null
    this.changedKeysTimerMiliSeconds = 10000
    this.isProcessingChangedKeys = false

    // Инициализируем Broadcast Channel для межтабной коммуникации
    this.broadcastChannel = new BroadcastChannel('indexeddb_channel')

    // Обработчик сообщений от других вкладок
    this.broadcastChannel.onmessage = (event) => {
      if (event.data && event.data.type === 'db_upgraded' && event.data.dbName === this.dbName) {
        // Принудительно закрываем старое соединение
        if (this.indexedDB) {
          this.indexedDB.close()
          this.indexedDB = null
        }
      }
    }

    // Обработка закрытия вкладки
    window.addEventListener('beforeunload', async () => {
      await this.processChangedKeys(true)
      if (this.changedKeysTimer) {
        clearTimeout(this.changedKeysTimer)
        this.changedKeysTimer = null
      }
    })
  }

  /**
   * Устанавливает ключ кеша и динамически обновляет имя базы данных.
   * Это нужно вызывать перед любой операцией с БД.
   * @param {string} userLogin - Логин текущего пользователя.
   */
  setCacheKey(userLogin) {
    userLogin = userLogin.toLowerCase()
    const newDbName = `db_${userLogin}`
    if (this.dbName !== newDbName) {
      if (this.indexedDB) {
        this.indexedDB.close()
        this.indexedDB = null
      }
      this.dbName = newDbName
    }
  }

  /**
   * Открывает соединение с IndexedDB.
   * Использует `dbName`, установленное методом `setCacheKey`.
   */
  async openDB() {
    if (!this.dbName) {
      throw new Error('Ключ кеша не установлен. Вызовите `setCacheKey` перед использованием БД.')
    }

    if (this.indexedDB && this.indexedDB.name === this.dbName) {
      return this.indexedDB
    }

    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(this.dbName, this.dbVersion)

      request.onerror = (event) => {
        reject(event.target.error)
      }

      request.onsuccess = (event) => {
        this.indexedDB = event.target.result
        resolve(this.indexedDB)
      }

      request.onupgradeneeded = (event) => {
        const db = event.target.result
        for (const storeName of Object.values(this.storeNames)) {
          if (!db.objectStoreNames.contains(storeName)) {
            let options = { keyPath: 'key' }
            db.createObjectStore(storeName, options)
          }
        }

        // Отправляем сообщение всем вкладкам о том, что БД была обновлена
        this.broadcastChannel.postMessage({ type: 'db_upgraded', dbName: this.dbName })
      }

      // Обработка блокировки открытия из-за другой вкладки
      request.onblocked = () => {
        if (this.indexedDB) {
          this.indexedDB.close()
        }
      }
    })
  }

  /**
   * Получает транзакцию для работы с объектным хранилищем.
   * @param {string} storeName - Название хранилища.
   * @param {string} mode - Режим доступа ('readonly' или 'readwrite').
   * @returns {IDBObjectStore} Объект хранилища.
   */
  async getObjectStore(storeName, mode = 'readonly') {
    await this.openDB()
    const transaction = this.indexedDB.transaction([storeName], mode)
    return transaction.objectStore(storeName)
  }

  /**
   * Очищает содержимое хранилища.
   * @param {string} storeName - Название хранилища для очистки.
   */
  async clearStore(storeName) {
    const storeObject = await this.getObjectStore(storeName, 'readwrite')
    return new Promise((resolve, reject) => {
      const request = storeObject.clear()
      request.onsuccess = () => resolve()
      request.onerror = (event) => reject(event.target.error)
    })
  }

  /**
   * Считывает все элементы из хранилища.
   * @param {string} storeName - Название хранилища.
   * @returns {Array} Массив элементов.
   */
  async readAllItems(storeName) {
    const storeObject = await this.getObjectStore(storeName, 'readonly')
    return new Promise((resolve, reject) => {
      const request = storeObject.getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = (event) => reject(event.target.error)
    })
  }

  /**
   * Считывает один элемент из хранилища по ключу.
   * @param {string} storeName - Название хранилища.
   * @param {*} key - Ключ элемента для считывания.
   * @param {*} defaultVal - Значение по умолчанию, если элемент не найден.
   * @returns {*} Найденный элемент или `defaultVal`.
   */
  async getItem(storeName, key, defaultVal) {
    const storeObject = await this.getObjectStore(storeName, 'readonly')
    return new Promise((resolve, reject) => {
      const request = storeObject.get(key)
      request.onsuccess = () => {
        if (request.result === undefined) {
          resolve(JSON.stringify(defaultVal))
        } else {
          resolve(request.result.value)
        }
      }
      request.onerror = (event) => {
        reject(event.target.error)
      }
    })
  }

  /**
   * Обновляет или добавляет элемент в хранилище.
   * @param {string} storeName - Название хранилища.
   * @param {object} value - Объект для обновления/добавления.
   */
  async setItem(storeName, value) {
    const storeObject = await this.getObjectStore(storeName, 'readwrite')
    const result = await new Promise((resolve, reject) => {
      const request = storeObject.put(value)
      request.onsuccess = () => resolve(true)
      request.onerror = (event) => reject(event.target.error)
    })

    if (storeName === this.storeNames.changedKeys) {
      if (!this.changedKeysTimer) {
        this.changedKeysTimer = setTimeout(() => {
          this.processChangedKeys()
        }, this.changedKeysTimerMiliSeconds)
      }
    }
    return result
  }

  /**
   * Обработка changedKeys: отправка изменений и очистка store
   * @param {boolean} force - если true, обработать немедленно (например, при закрытии вкладки)
   */
  async processChangedKeys(force = false) {
    if (!this.dbName) { return }
    let hasStorageError = await this.getItem(this.storeNames.dopProps, 'errorState', false)
    if (this.isProcessingChangedKeys || (hasStorageError && !force)) { return }
    this.isProcessingChangedKeys = true
    if (this.changedKeysTimer) {
      clearTimeout(this.changedKeysTimer)
      this.changedKeysTimer = null
    }
    try {
      const changedItems = await this.readAllItems(this.storeNames.changedKeys)
      if (!changedItems || !changedItems.length) {
        this.isProcessingChangedKeys = false
        return
      }

      const updateItemKeys = []
      const deleteItemKeys = []
      for (const item of changedItems) {
        if (!item) {
          continue
        }
        if (item.state === 'update') {
          updateItemKeys.push(item.key)
        } else if (item.state === 'delete') {
          deleteItemKeys.push(item.key)
        }
      }
      let updateResult, deleteResult
      if (updateItemKeys.length) {
        updateResult = await this.processChangedKeysUpdate(updateItemKeys)
        if (!updateResult) {
          this.setItem(this.storeNames.dopProps, {key: 'errorState', value: true})
          return
        }
      }
      if (deleteItemKeys.length) {
        deleteResult = await this.processChangedKeysDelete(deleteItemKeys)
        if (!deleteResult) {
          this.setItem(this.storeNames.dopProps, {key: 'errorState', value: true})
          return
        }
      }
    } catch (e) {
      Vue.prototype.$serror('processChangedKeys Ошибка processChangedKeys:', e)
    } finally {
      this.isProcessingChangedKeys = false
    }
  }

  // Обработка обновлений
  async processChangedKeysUpdate(updateItemKeys) {
    const allItemProps = await this.readAllItems(this.storeNames.itemProps)
    let cachedData = allItemProps.filter(item => updateItemKeys.includes(item.key))
    cachedData = cachedData?.map((resultItem) => {
      delete resultItem.dateCreated
      delete resultItem.__typename
      return resultItem
    })
    const updateResult = await settingsApi.setManyDBStorage(cachedData)
    if (updateResult) {
      await Promise.all(updateItemKeys.map(key => this.deleteItem(this.storeNames.changedKeys, key)))
    }
    return updateResult
  }

  // Обработка удалений
  async processChangedKeysDelete(deleteItemKeys) {
    const deleteResult = await settingsApi.deleteManyDBStorage(deleteItemKeys)
    if (deleteResult) {
      await Promise.all(deleteItemKeys.map(key => this.deleteItem(this.storeNames.changedKeys, key)))
    }
    return deleteResult
  }

  /**
   * Обновляет или добавляет несколько элементов в одном хранилище.
   * @param {string} storeName - Название хранилища.
   * @param {Array<object>} items - Массив объектов для обновления/добавления.
   */
  async setItems(storeName, items) {
    const transaction = this.indexedDB.transaction([storeName], 'readwrite')
    const storeObject = transaction.objectStore(storeName)

    for (const item of items) {
      storeObject.put(item)
    }

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        resolve()
      }
      transaction.onerror = (event) => {
        reject(event.target.error)
      }
    })
  }

  /**
   * Удаляет элемент из хранилища по ключу.
   * @param {string} storeName - Название хранилища.
   * @param {*} key - Ключ элемента для удаления.
   */
  async deleteItem(storeName, key) {
    const storeObject = await this.getObjectStore(storeName, 'readwrite')
    return new Promise((resolve, reject) => {
      const request = storeObject.delete(key)
      request.onsuccess = () => resolve()
      request.onerror = (event) => reject(event.target.error)
    })
  }
}

export default new IndexedDBService()
